import { Avatar, Box } from "@mui/material";
import DataTable from "app/components/DataTable";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { selectCustomerStatusLabelKey } from "../../redux/selector";
import { Customer } from "../../types";
import { useStyles } from "./styles";

type Props = {
  rows: Customer[];
  loading?: boolean;
};

const formatCurrency = (value: number) => {
  return `$${value.toLocaleString()}`;
};

const formatCellValue = (value: string | number | null | undefined) => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }

  return value;
};

const getContactInitials = (name?: string) => {
  if (!name?.trim()) {
    return "";
  }

  return name
    .trim()
    .split(/\s+/)
    .map((part) => part[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
};

const CustomersTable = ({ rows, loading = false }: Props) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const navigate = useNavigate();

  const handleRowClick = (row: Customer) => {
    navigate(`/customers/${row.customerId}`);
  };

  const columns = useMemo(
    () => [
      {
        id: "status",
        label: t("CUSTOMERS.TABLE.STATUS"),
        minWidth: 110,
        align: "right",
        format: (row: Customer) => (
          <span className={styles.statusBadge}>
            {t(selectCustomerStatusLabelKey(row.status))}
          </span>
        ),
      },
      {
        id: "totalRevenue",
        label: t("CUSTOMERS.TABLE.TOTAL_INCOME"),
        minWidth: 140,
        align: "right",
        format: (row: Customer) => formatCurrency(row.totalRevenue ?? 0),
      },
      {
        id: "activeWorkOrdersCount",
        label: t("CUSTOMERS.TABLE.ACTIVE_WORK_ORDERS"),
        minWidth: 140,
        align: "right",
        format: (row: Customer) =>
          row.activeWorkOrdersCount > 0 ? row.activeWorkOrdersCount : "-",
      },
      {
        id: "contactPhone",
        label: t("CUSTOMERS.TABLE.CONTACT_PHONE"),
        minWidth: 160,
        align: "right",
        format: (row: Customer) => formatCellValue(row.contactPhone),
      },
      {
        id: "contactPersonName",
        label: t("CUSTOMERS.TABLE.CONTACT_PERSON"),
        minWidth: 180,
        align: "right",
        format: (row: Customer) => (
          <Box className={styles.contactCell}>
            <span>{formatCellValue(row.contactPersonName)}</span>
            <Avatar className={styles.avatar}>
              {getContactInitials(row.contactPersonName)}
            </Avatar>
          </Box>
        ),
      },
      {
        id: "category",
        label: t("CUSTOMERS.TABLE.CATEGORY"),
        minWidth: 200,
        align: "right",
        format: (row: Customer) => formatCellValue(row.category),
      },
      {
        id: "customerName",
        label: t("CUSTOMERS.TABLE.CUSTOMER_NAME"),
        minWidth: 240,
        align: "right",
        format: (row: Customer) => formatCellValue(row.customerName),
      },
    ],
    [styles, t]
  );

  return (
    <div className={styles.root}>
      <DataTable
        rows={rows}
        columns={columns}
        loading={loading && rows.length === 0}
        serialEnabled={false}
        noDataLabel={t("CUSTOMERS.EMPTY")}
        isPointerCursor
        onRowClick={handleRowClick}
      />
    </div>
  );
};

export default CustomersTable;
